import type { ReactNode } from 'react'

/**
 * The canonical sitemap — the `NAV` array from `app-shell.js`, unchanged in order and
 * grouping, with hrefs mapped onto router paths.
 */

const ic = (d: ReactNode) => (
  <svg
    className="ni-ico"
    width="17"
    height="17"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    {d}
  </svg>
)

export const I = {
  home: ic(
    <>
      <path d="M3 10l9-7 9 7" />
      <path d="M5 9v11h14V9" />
    </>,
  ),
  positions: ic(
    <>
      <path d="M3 3v18h18" />
      <path d="M7 15l4-5 3 3 5-7" />
    </>,
  ),
  markets: ic(
    <>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4-4" />
    </>,
  ),
  ticket: ic(
    <path d="M4 8a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 000 4v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2a2 2 0 000-4z" />,
  ),
  blotter: ic(<path d="M4 5h16M4 12h16M4 19h16" />),
  decision: ic(
    <>
      <path d="M9 6l6 6-6 6" />
      <path d="M4 12h5" />
    </>,
  ),
  strategy: ic(
    <>
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="18" cy="18" r="2.5" />
      <path d="M8 6h6a4 4 0 014 4v6" />
    </>,
  ),
  backtest: ic(
    <>
      <path d="M3 3v18h18" />
      <rect x="7" y="10" width="3" height="8" />
      <rect x="12" y="6" width="3" height="12" />
      <rect x="17" y="13" width="3" height="5" />
    </>,
  ),
  risk: ic(
    <>
      <path d="M12 3l8 4v5c0 5-3.5 8-8 9-4.5-1-8-4-8-9V7z" />
      <path d="M12 8v4M12 15h.01" />
    </>,
  ),
  perf: ic(
    <>
      <path d="M3 17l6-6 4 4 8-8" />
      <path d="M15 7h6v6" />
    </>,
  ),
  health: ic(<path d="M3 12h4l2 5 4-12 2 7h6" />),
  recon: ic(
    <>
      <path d="M4 4v6h6" />
      <path d="M20 20v-6h-6" />
      <path d="M4 10a8 8 0 0114-3M20 14a8 8 0 01-14 3" />
    </>,
  ),
  alerts: ic(
    <>
      <path d="M6 8a6 6 0 1112 0c0 7 3 8 3 8H3s3-1 3-8" />
      <path d="M10 21a2 2 0 004 0" />
    </>,
  ),
  audit: ic(
    <>
      <path d="M6 3h9l5 5v13H6z" />
      <path d="M9 12h7M9 16h7" />
    </>,
  ),
  settings: ic(
    <>
      <circle cx="12" cy="12" r="3" />
      <path d="M19 12a7 7 0 00-.1-1.3l2-1.6-2-3.4-2.4 1a7 7 0 00-2.2-1.3L14 2h-4l-.3 2.4a7 7 0 00-2.2 1.3l-2.4-1-2 3.4 2 1.6A7 7 0 005 12a7 7 0 00.1 1.3l-2 1.6 2 3.4 2.4-1a7 7 0 002.2 1.3L10 22h4l.3-2.4a7 7 0 002.2-1.3l2.4 1 2-3.4-2-1.6A7 7 0 0019 12z" />
    </>,
  ),
  onboard: ic(
    <>
      <path d="M9 11l3 3L22 4" />
      <path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" />
    </>,
  ),
  admin: ic(
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M4 21a8 8 0 0116 0" />
    </>,
  ),
  bankroll: ic(
    <>
      <rect x="3" y="6" width="18" height="13" rx="2" />
      <path d="M3 10h18M16 15h2" />
    </>,
  ),
  leaderboard: ic(
    <>
      <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 01-10 0z" />
      <path d="M7 6H4a3 3 0 003 4M17 6h3a3 3 0 01-3 4" />
    </>,
  ),
}

export interface NavItem {
  key: string
  label: string
  path: string
  icon: ReactNode
}

/** `bottom` pins a section to the foot of the sidebar instead of stacking it under the rest. */
export const NAV: { section: string; bottom?: boolean; items: NavItem[] }[] = [
  {
    section: 'Trading',
    items: [
      { key: 'home', label: 'Today', path: '/', icon: I.home },
      { key: 'positions', label: 'Positions', path: '/positions', icon: I.positions },
      { key: 'pnl', label: 'P&L', path: '/pnl', icon: I.perf },
      { key: 'markets', label: 'Markets', path: '/markets', icon: I.markets },
      { key: 'betslip', label: 'Bet slip', path: '/bet-slip', icon: I.ticket },
      { key: 'bankroll', label: 'Bankroll', path: '/bankroll', icon: I.bankroll },
      { key: 'leaderboard', label: 'Leaderboard', path: '/leaderboard', icon: I.leaderboard },
    ],
  },
  {
    section: 'Intelligence',
    items: [
      { key: 'strategies', label: 'Strategies', path: '/strategies', icon: I.strategy },
      { key: 'backtests', label: 'Backtests', path: '/backtests', icon: I.backtest },
      { key: 'perf', label: 'Performance', path: '/performance', icon: I.perf },
    ],
  },
  {
    section: 'Account',
    bottom: true,
    items: [
      { key: 'settings', label: 'Settings', path: '/settings/profile', icon: I.settings },
      { key: 'profile', label: 'Profile', path: '/profile', icon: I.admin },
    ],
  },
]
